import { useState } from "react";
import axios from "axios";
import { Mail, Linkedin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { profile } from "../data/profile";
import { Reveal, Overline, AnimatedHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Your enquiry has been sent successfully.");
      setForm({ name: "", email: "", phone: "", organization: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-[#E2E5E1] bg-bone px-4 py-3 text-sm text-navy placeholder:text-[#9aa6ad] focus:outline-none focus:ring-1 focus:ring-navy focus:border-navy transition";

  return (
    <section id="contact" className="py-16 sm:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Get in Touch</Overline>
              <AnimatedHeading className="font-display text-3xl sm:text-4xl font-bold text-navy tracking-tight mt-4 leading-snug">
                Let's discuss your healthcare project.
              </AnimatedHeading>
              <p className="mt-4 text-lg" style={{ color: "#4B5A69" }}>
                For consultancy, advisory or speaking engagements, reach out and Dr. Pandya's office will respond promptly.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="mt-10 space-y-5">
              <a
                href={`mailto:${profile.contact.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <span className="h-11 w-11 shrink-0 rounded-xl bg-navy/5 flex items-center justify-center text-teal group-hover:bg-navy group-hover:text-bone transition-colors">
                  <Mail size={20} />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-plex uppercase tracking-widest text-gold">Email</span>
                  <span className="block text-navy font-medium text-sm sm:text-base break-all">{profile.contact.email}</span>
                </span>
              </a>
              <a
                href={profile.contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="flex items-center gap-4 group"
              >
                <span className="h-11 w-11 shrink-0 rounded-xl bg-navy/5 flex items-center justify-center text-teal group-hover:bg-navy group-hover:text-bone transition-colors">
                  <Linkedin size={20} />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-plex uppercase tracking-widest text-gold">LinkedIn</span>
                  <span className="block text-navy font-medium text-sm sm:text-base">Connect with Dr. Vijay Pandya</span>
                </span>
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white border border-[#E2E5E1] rounded-2xl p-8 sm:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Full Name *</label>
                    <input data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Email *</label>
                    <input data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Phone</label>
                    <input data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Organization</label>
                    <input data-testid="contact-org-input" className={inputCls} value={form.organization} onChange={set("organization")} placeholder="Hospital / Institution" />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="block text-sm font-medium text-navy mb-1.5">Subject</label>
                  <input data-testid="contact-subject-input" className={inputCls} value={form.subject} onChange={set("subject")} placeholder="How can Dr. Pandya help?" />
                </div>
                <div className="mt-5">
                  <label className="block text-sm font-medium text-navy mb-1.5">Message *</label>
                  <textarea data-testid="contact-message-input" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your requirement..." />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="mt-7 inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-md bg-navy text-bone font-medium hover:bg-teal transition-colors disabled:opacity-60 w-full sm:w-auto"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {loading ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
